<!--
  Structure de la carte TCG à double face avec flip 3D.

  Hiérarchie DOM :
  .card-scene                   — conteneur racine, porte les CSS vars de rareté
  ├── .card-glow                — halo de lueur externe (caché côté dos)
  ├── .particles-container      — particules ambiantes flottantes
  └── .card-wrap                — pivot GSAP (rotationY 0=face / 180=dos)
      ├── .card-shell           — masque extérieur + spinner de bordure animé
      └── .card-inner           — fond sombre, preserve-3d
          ├── .card-face.card-front  — face recto : image, HUD, score, nom
          └── .card-face.card-back   — face verso : SVG fractures + logo PEPS
-->

<div
  class="card-scene"
  [attr.data-rarity]="rarity"
  [class.card-face-down]="initiallyFlipped"
  [class.card-static]="staticDisplay"
  [class.card-paused]="paused"
  #scene
>
  <!-- Halo de lueur externe – couleur pilotée par --glow-bg -->
  <div class="card-glow"></div>

  <!-- Particules ambiantes génériques (toutes raretés) -->
  <div class="particles-container" #particlesContainer></div>

  <!-- Pivot 3D animé par GSAP -->
  <div class="card-wrap" #cardWrap>

    <!-- Coque + spinner de bordure animé -->
    <div class="card-shell">
      <div class="border-spinner"></div>
    </div>

    <!-- Corps de la carte, preserve-3d -->
    <div class="card-inner">

      <!-- ── FACE RECTO ─────────────────────────────────────────────────── -->
      <div class="card-face card-front" #cardFront>
       <!-- Clip interne : le clipping est séparé de .card-face pour que
            Firefox honore backface-visibility (ignoré si overflow != visible). -->
       <div class="card-face-clip">

        <!-- Image principale -->
        <img
          class="card-image"
          [src]="minioBaseUrl + image"
          [alt]="name"
          loading="lazy"
          decoding="async"
          (load)="onImageLoad()"
          (error)="onImageLoad()"
        >

        <!-- Loader tant que l'image recto n'est pas chargée (désactivé pendant l'ouverture de booster) -->
        @if (showLoader && !imageLoaded()) {
          <div class="card-image-loader">
            <span class="card-image-spinner"></span>
          </div>
        }

        <!-- Superpositions décoratives -->
        <div class="gradient-overlay"></div>
        <div class="scanlines"></div>
       @if(author) {
	       <span class="author-tag">Art by {{ author}}</span>
       }
        <!-- Zone haute : score et logo -->
        <div class="card-top">
          <div class="card-score" #scoreEl>
            <span class="score-val">{{ score }}</span>
          </div>
          <img [src]="'/assets/images/cards/card_icon/' + extensionId + '.png'" class="ow-logo" alt="logo" #logoEl>
        </div>

        <!-- Zone basse : nom, rareté, étoiles -->
        <div class="card-bottom">
          <h2 class="card-name" #nameEl>{{ name }}</h2>
          <div class="card-meta">
            <div class="stars-row" #starsEl>
              @for (s of filledStars; track $index) {
                <span class="star" [innerHTML]="pepiteSafe"></span>
              }
              @if (emptyStars.length > 0) {
                <span class="empty-stars">
                  @for (s of emptyStars; track $index) {
                    <span class="star" [innerHTML]="pepiteBlackSafe"></span>
                  }
                </span>
              }
            </div>
          </div>
        </div>

        <!-- Reflet holographique animé -->
        <div class="holo-sweep"></div>

        <!-- ── Coins HUD (apparaissent après le retournement) ─────────── -->
        <div class="corner-hud c-tl" #cornerTL>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path
              class="hud-path"
              d="M 5 5 L 40 5 L 45 10 L 10 10 L 10 45 L 5 40 Z"
              style="--hud-delay: 0s"
            />
          </svg>
        </div>

        <div class="corner-hud c-tr" #cornerTR>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path
              class="hud-path"
              d="M 5 5 L 40 5 L 45 10 L 10 10 L 10 45 L 5 40 Z"
              style="--hud-delay: 0.2s"
            />
          </svg>
        </div>

        <div class="corner-hud c-bl" #cornerBL>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path
              class="hud-path"
              d="M 5 5 L 40 5 L 45 10 L 10 10 L 10 45 L 5 40 Z"
              style="--hud-delay: 0.4s"
            />
          </svg>
        </div>

        <div class="corner-hud c-br" #cornerBR>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <path
              class="hud-path"
              d="M 5 5 L 40 5 L 45 10 L 10 10 L 10 45 L 5 40 Z"
              style="--hud-delay: 0.6s"
            />
          </svg>
        </div>

        <!-- ── Effets spéciaux par rareté ──────────────────────────────── -->

        <!-- Paillettes montantes : collector + legendary -->
        @if (hasGlitterEffect) {
          <div class="particle-from-bottom" #particleFromBottom></div>
        }

        <!-- Couche d'étincelles color-dodge : epic + legendary + collector -->
        @if (hasSparkleEffect) {
          <div class="random-light-layer" #randomLightLayer></div>
        }

        <!-- Canvas foudre : collector uniquement -->
        @if (hasLightningEffect) {
          <canvas class="lightning-canvas" #lightningCanvas></canvas>
        }

       </div><!-- /.card-face-clip -->
      </div><!-- /.card-front -->

      <!-- ── FACE VERSO ─────────────────────────────────────────────────── -->
      <div class="card-face card-back" [style.background-image]="'url(/assets/images/cards/card_back/' + backOfCard + '.png)'" #cardBack></div>

    </div><!-- /.card-inner -->
  </div><!-- /.card-wrap -->
</div><!-- /.card-scene -->
